import React, { forwardRef, useEffect, useState } from "react";
import BaseInput from "@reusables/BaseInput";

interface AllocationQuantityInputProps {
    quantity: number;
    /**
     * Called on blur / Enter with the typed number, or null when the value is empty or not positive.
     * Bounding is up to the parent, as it depends on the other allocations of the product.
     */
    onCommit: (quantity: number | null) => void;
}

/**
 * Quantity input that keeps a local draft while focused, so that the form is updated only once the value is committed.
 */
const AllocationQuantityInput = forwardRef<HTMLInputElement, AllocationQuantityInputProps>(
    function AllocationQuantityInput({ quantity, onCommit }, ref) {
        const [draftValue, setDraftValue] = useState<string>(quantity.toString());
        const [isFocused, setIsFocused] = useState(false);

        // Sync draft value with the actual quantity when not focused
        useEffect(() => {
            if (!isFocused) {
                setDraftValue(quantity.toString());
            }
        }, [quantity, isFocused]);

        const handleBlur = () => {
            setIsFocused(false);

            const numericValue = parseFloat(draftValue.trim());

            onCommit(isNaN(numericValue) || numericValue <= 0 ? null : numericValue);
        };

        return (
            <BaseInput
                ref={ref}
                value={isFocused ? draftValue : quantity.toString()}
                type="number"
                onChange={(value) =>
                    setDraftValue(value === null || value === undefined ? "" : String(value))
                }
                onFocus={() => {
                    setIsFocused(true);
                    setDraftValue(quantity.toString());
                }}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                    }
                }}
                hideArrows
                sx={{
                    "& input": {
                        padding: "8px 12px",
                        textAlign: "right",
                    },
                }}
            />
        );
    }
);

export default AllocationQuantityInput;
